import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import { enqueueSnackbar } from "notistack";
import React, { ChangeEvent, ReactElement, useState } from "react";

import { Collection } from "../../../generated/gql/graphql";
import useCreateCollection from "../../../hooks/mutation/collection/use-create-collection";
import useText from "../../../hooks/use-text";
import { LocalizedStrings, SupportedLanguages } from "../../../utils/lang";

type Props = {
  open: boolean;
  redirectAfterCreation?: boolean;
  onClose(): void;
};

export default function CreateCollectionDialog(props: Props): ReactElement {
  const { open, redirectAfterCreation, onClose } = props;

  const { strings } = useText(local);
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createCollection } = useCreateCollection();

  async function onSave(): Promise<void> {
    setIsSubmitting(true);

    try {
      const { data } = (await createCollection({
        name: name,
      })) as { data: { createCollection: Collection } };

      const { createCollection: collection } = data as {
        createCollection: Collection;
      };

      if (redirectAfterCreation) {
        window.location.href = `/collection/${collection.id}`;
      }

      enqueueSnackbar(strings.info.success, { variant: "success" });
      onClose();
    } catch (err) {
      console.error(err);
      enqueueSnackbar(strings.info.failed, { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{strings.header}</DialogTitle>
      <DialogContent className="min-w-[300px]">
        <TextField
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
          autoFocus
          focused
          label={strings.form.name.label}
          margin="dense"
          type="text"
          fullWidth
          variant="standard"
          placeholder={strings.form.name.placeholder}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.form.abort}</Button>
        <Button
          disabled={!name?.replace(/\s/g, "").length || isSubmitting}
          onClick={onSave}
        >
          {isSubmitting ? (
            <Box className="flex w-full justify-center">
              <CircularProgress size="1rem" className="w-1 h-1" />
            </Box>
          ) : (
            <span>{strings.form.submit}</span>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type LocalSchema = {
  header: string;
  form: {
    name: {
      label: string;
      placeholder: string;
    };
    submit: string;
    abort: string;
  };
  info: {
    success: string;
    failed: string;
  };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    header: "Add new collection",
    form: {
      name: {
        label: "Collection Name",
        placeholder: "Enter name",
      },
      submit: "Add",
      abort: "Cancel",
    },
    info: {
      success: "Collection created successfully",
      failed: "Collection created failed",
    },
  },
};
